import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useGetMyCompanies } from "./useGetMyCompanies";
import { HealthcareCompanyType } from "@shared/types/commonTypes";
import { useParams } from "react-router-dom";
import { formatNumber } from "@features/clinics/utils";

export type Currency = {
  iso: string;
  name: string;
  symbol: string;
  format: string;
  valueFormatLocale: string;
};

type UseFormattedCurrencyProps = {
  value: string;
  currencyIso?: string;
  decimalPlaces?: number;
};

const useFormattedCurrency = ({
  value,
  currencyIso,
  decimalPlaces = 2,
}: UseFormattedCurrencyProps) => {
  const { GET } = useApiRequest();
  const { clinicId } = useParams();

  const { companies } = useGetMyCompanies({
    type: HealthcareCompanyType.DENTAL_CLINIC,
    enabled: true,
  });

  const selectedClinic = useMemo(() => {
    return companies?.find((clinic) => clinic.id === clinicId);
  }, [companies, clinicId]);

  const { data: currencies = [] } = useQuery<Currency[]>({
    queryKey: ["currencies"],
    queryFn: () => GET("/api/metadata/currencies"),
    // Currencies are static metadata - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  return useMemo(() => {
    const currency = currencies.find(
      (c) => c.iso === (currencyIso || selectedClinic?.currency)
    );

    if (!value)
      return {
        formattedValue: "",
        rawValue: value,
        currency: currency?.iso,
        symbol: currency?.symbol,
      };
    if (!currency) return { formattedValue: "", rawValue: value };

    if (currency.valueFormatLocale) {
      const formattedValue = new Intl.NumberFormat(currency.valueFormatLocale, {
        style: "currency",
        currency: currency.iso,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(Number(value));

      return {
        formattedValue,
        rawValue: value,
        currency: currency?.iso,
        symbol: currency?.symbol,
        formattedNumber: formatNumber(
          Number(value),
          currency?.valueFormatLocale
        ),
      };
    }

    // Fallback to using the format template if no locale is specified
    const formattedValue = currency.format.replace(
      "{{value}}",
      Number(value).toFixed(decimalPlaces)
    );
    return {
      formattedValue,
      rawValue: value,
      currency: currency?.iso,
      symbol: currency?.symbol,
      formattedNumber: formatNumber(Number(value), currency?.valueFormatLocale),
    };
  }, [value, currencyIso, selectedClinic?.currency, currencies, decimalPlaces]);
};

export default useFormattedCurrency;
